// Language detection and management utilities

export const SUPPORTED_LANGUAGES = ['en', 'zh', 'ja', 'hi', 'id', 'es', 'ar', 'ms', 'fr', 'bn', 'ru', 'pt', 'de', 'ur', 'pa', 'fa', 'sw'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const LANGUAGE_NAMES = {
  en: 'English',
  zh: '中文',
  ja: '日本語',
  hi: 'हिंदी',
  id: 'Bahasa Indonesia',
  es: 'Español',
  ar: 'العربية',
  ms: 'Bahasa Melayu',
  fr: 'Français',
  bn: 'বাংলা',
  ru: 'Русский',
  pt: 'Português',
  de: 'Deutsch',
  ur: 'اردو',
  pa: 'ਪੰਜਾਬੀ',
  fa: 'فارسی',
  sw: 'Kiswahili'
} as const;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Language storage key
const LANGUAGE_STORAGE_KEY = 'timer-today-language';

/**
 * Detect user's preferred language
 * Priority: 1. URL parameter 2. localStorage 3. Browser language 4. Default
 */
export function detectLanguage(): SupportedLanguage {
  // 1. Check URL parameter (for future implementation)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang as SupportedLanguage)) {
    return urlLang as SupportedLanguage;
  }

  // 2. Check localStorage
  try {
    const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang as SupportedLanguage)) {
      return storedLang as SupportedLanguage;
    }
  } catch (error) {
    // localStorage might not be available
    console.warn('Failed to access localStorage:', error);
  }

  // 3. Check browser language
  const browserLang = navigator.language.toLowerCase();
  
  // Check for exact match first
  if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }
  
  // Check for language prefix match (e.g., 'zh-CN' matches 'zh')
  const langPrefix = browserLang.split('-')[0];
  if (SUPPORTED_LANGUAGES.includes(langPrefix as SupportedLanguage)) {
    return langPrefix as SupportedLanguage;
  }
  
  // Check additional browser languages
  for (const lang of navigator.languages || []) {
    const normalizedLang = lang.toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(normalizedLang as SupportedLanguage)) {
      return normalizedLang as SupportedLanguage;
    }
    
    const prefix = normalizedLang.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(prefix as SupportedLanguage)) {
      return prefix as SupportedLanguage;
    }
  }

  // 4. Return default language
  return DEFAULT_LANGUAGE;
}

/**
 * Save language preference to localStorage
 */
export function saveLanguagePreference(language: SupportedLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to save language preference:', error);
  }
}

/**
 * Get saved language preference from localStorage
 */
export function getSavedLanguage(): SupportedLanguage | null {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage)) {
      return saved as SupportedLanguage;
    }
  } catch (error) {
    console.warn('Failed to get saved language:', error);
  }
  return null;
}

/**
 * Language data for the language selector modal
 */
export const LANGUAGE_OPTIONS = [
  // Supported languages
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'zh', name: '中文', flag: '🇨🇳', nativeName: '中文' },
  { code: 'es', name: 'Español', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳', nativeName: 'हिंदी' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', nativeName: 'العربية' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩', nativeName: 'বাংলা' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', nativeName: 'Русский' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', nativeName: 'Português' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', nativeName: 'Bahasa Indonesia' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰', nativeName: 'اردو' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', nativeName: '日本語' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '🇮🇳', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', nativeName: 'Bahasa Melayu' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷', nativeName: 'فارسی' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪', nativeName: 'Kiswahili' }
] as const;

/**
 * Check if a language is currently supported
 */
export function isLanguageSupported(language: string): language is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
}

/**
 * Get language direction (LTR/RTL)
 */
export function getLanguageDirection(language: SupportedLanguage): 'ltr' | 'rtl' {
  const rtlLanguages: SupportedLanguage[] = ['ar', 'ur', 'fa']; // Arabic, Urdu, Persian
  return rtlLanguages.includes(language) ? 'rtl' : 'ltr';
}